import React, { useRef } from 'react';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { FaPrint, FaReceipt, FaDownload } from 'react-icons/fa';
import { useQZTray } from '../../context/QZTrayContext';
import { useTranslation } from '../../context/LanguageContext';
import createSalesReportThermal from './thermal-templates/SalesReportThermal';
import qz from 'qz-tray';
import axios from '../../utils/axios';

// Print-specific styles
const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        .sales-report,
        .sales-report * {
            visibility: visible;
        }
        .sales-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
        }
        .no-print {
            display: none !important;
        }
        table {
            page-break-inside: auto;
        }
        tr {
            page-break-inside: avoid;
            page-break-after: auto;
        }
        thead {
            display: table-header-group;
        }
    }
`;

const SalesReport = ({ data, language, settings }) => {
    // eslint-disable-next-line no-unused-vars
    const { t } = useTranslation();
    const { isConnected, connectQZ, printers } = useQZTray();
    // eslint-disable-next-line no-unused-vars
    const printerRef = useRef(null);

    if (!data?.daily_sales || !data?.top_items || !data?.summary) return null;

    // Ensure returns data exists
    const returns = data.returns || { total_amount: 0, count: 0, percentage: 0 };
    
    // Process return orders data if available
    const hasReturns = returns && (returns.count > 0 || returns.total_amount > 0);

    // Debug returns data


    const handlePrint = () => {
        window.print();
    };
    
    const handleDownload = async () => {
        try {
            // Get the current date range from the URL or use default
            const urlParams = new URLSearchParams(window.location.search);
            const startDate = urlParams.get('start_date') || new Date().toISOString().split('T')[0];
            const endDate = urlParams.get('end_date') || new Date().toISOString().split('T')[0];
            
            // Create the API URL for Excel download
            const apiUrl = `reports/generate`;
            
            // Use the same data that's already loaded in the UI
            // This ensures the Excel file matches what's displayed
            if (data && Object.keys(data).length > 0) {
                // Create a new Blob with the JSON data
                const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                
                // Create a FormData object
                const formData = new FormData();
                formData.append('report_type', 'sales');
                formData.append('start_date', startDate);
                formData.append('end_date', endDate);
                formData.append('format', 'excel');
                formData.append('data', jsonBlob);
                
                // Fetch the file as a blob
                const response = await axios.post(apiUrl, {
                    report_type: 'sales',
                    start_date: startDate,
                    end_date: endDate,
                    format: 'excel',
                        // Send the current data to ensure consistency
                    // Log the data being sent to verify top_items are included
                    current_data: JSON.stringify(data, null, 2)
                }, {
                    responseType: 'blob'
                });
                
                // Create a download link and trigger the download
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `sales_report_${startDate}_to_${endDate}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                alert(language === 'ar' ? 'لا توجد بيانات للتحميل' : 'No data to download');
            }
        } catch (error) {
            console.error('Download error:', error);
            alert(language === 'ar' ? 'خطأ في تحميل التقرير' : 'Download error');
        }
    };

    const handleThermalPrint = async () => {
        if (!settings || !settings.printing || !settings.printing.printer_name) {
            console.error('Printer not configured in settings');
            return;
        }

        if (!isConnected) {
            try {
                await connectQZ();
            } catch (err) {
                console.error('Failed to connect to printer:', err);
                return;
            }
        }

        // Debug returns data before thermal printing


        const thermalContent = createSalesReportThermal(data, language, settings);

        const { printing } = settings;
        const paperWidth = printing.paper_size || '80mm';
        const paperHeight = printing.paper_height || 'auto';
        // eslint-disable-next-line no-unused-vars
        const fontSize = printing.font_size || '10';

        // Calculate dimensions for printer config
        let pageWidth = parseInt(paperWidth);
        if (isNaN(pageWidth)) {
            console.error('Invalid paper width:', paperWidth);
            pageWidth = 80; // Use 80mm as default width
        }
        
        // Parse height properly - critical for preventing infinite printing
        let pageHeight;
        if (paperHeight === 'auto') {
            pageHeight = 300; // Use 300mm as default height for 'auto'

        } else {
            pageHeight = parseInt(paperHeight.replace('mm', ''));
            if (isNaN(pageHeight)) {
                console.error('Invalid paper height:', paperHeight);
                pageHeight = 300; // Use 300mm as default height
            }
        }


        // Find the matching printer
        const matchingPrinter = printers.find(printer => 
            printer.value === printing.printer_name || 
            printer.label === printing.printer_name
        );

        if (!matchingPrinter) {
            console.error(`Selected printer "${printing.printer_name}" not found in available printers:`, printers);
            return;
        }

        // Create base printer config
        const config = qz.configs.create(matchingPrinter.value);
        
        // Set essential print options - this is critical for fixing infinite printing
        config.reconfigure({
            size: { width: pageWidth, height: pageHeight },
            margins: { top: 0, right: 2, bottom: 0, left: 2 },
            colorType: 'grayscale',
            orientation: 'portrait',
            units: 'mm',
            density: '180dpi',
            scaleContent: true, // Set to true to ensure content fits properly
            bounds: {
                width: pageWidth,
                height: pageHeight
            }
        });

        // Create print data
        const printData = [{
            type: 'html',
            format: 'plain',
            data: thermalContent,
            options: {
                pageWidth: `${pageWidth}mm`,
                pageHeight: `${pageHeight}mm`,
                css: true
            }
        }];

        // Print the report
        qz.print(config, printData)
            .then(() => {
    
            })
            .catch(err => {
                console.error('[SalesReport] Print failed:', err);
            });
    };

    return (
        <div className="sales-report mt-8 space-y-8" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <style>{printStyles}</style>
            {/* Date Range Indicator */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-blue-700">
                            {language === 'ar' ? 'نطاق التقرير:' : 'Report Range:'} 
                            <span className="font-medium ml-1">
                                {(() => {
                                    try {
                                        // Try to extract and format dates from the raw data
                                        if (data && data._meta && data._meta.date_range) {
                                            const { start_date, end_date } = data._meta.date_range;
                                            // Parse dates from ISO strings ensuring proper timezone handling
                                            const startDate = new Date(start_date);
                                            const endDate = new Date(end_date);
                                            
                                            // Always use Gregorian calendar format (en-US) but with Arabic numerals when needed
                                            // This ensures dates are shown in Gregorian calendar not Hijri
                                            const dateTimeFormat = new Intl.DateTimeFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                timeZoneName: 'short',
                                                calendar: 'gregory' // Force Gregorian calendar
                                            });
                                            
                                            // Format dates with timezone info to ensure consistency
                                            return `${dateTimeFormat.format(startDate)} - ${dateTimeFormat.format(endDate)}`;
                                        }
                                        return language === 'ar' ? 'النطاق الزمني المحدد' : 'Selected Time Range';
                                    } catch (e) {
                                        return language === 'ar' ? 'النطاق الزمني المحدد' : 'Selected Time Range';
                                    }
                                })()}
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Print Button */}
            <div className="flex justify-end space-x-4 no-print" style={{ direction: 'ltr' }}>
                <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                    <FaDownload className="mr-2" />
                    {language === 'ar' ? 'تحميل التقرير' : 'Download Report'}
                </button>
                <button
                    onClick={handleThermalPrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                    <FaReceipt className="mr-2" />
                    {language === 'ar' ? 'طباعة حرارية' : 'Thermal Print'}
                </button>
                <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <FaPrint className="mr-2" />
                    {language === 'ar' ? 'طباعة التقرير' : 'Print Report'}
                </button>
            </div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatNumber(data.summary.total_orders)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'متوسط قيمة الطلب' : 'Average Order Value'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatCurrency(data.summary.avg_order_value)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'إجمالي المبيعات' : 'Total Revenue'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatCurrency(data.summary.total_revenue)}
                    </p>
                </div>
            </div>

            {/* Return Orders Summary (if available) */}
            {hasReturns && (
                <div className="bg-red-50 rounded-lg p-6 border border-red-100">
                    <h3 className="text-lg font-semibold mb-3 text-red-700">
                        {language === 'ar' ? 'ملخص المرتجعات' : 'Returns Summary'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'إجمالي المرتجعات' : "Total Returns Amount"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatCurrency(returns.total_amount || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'عدد الطلبات المرتجعة' : "Number of Returns"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatNumber(returns.count || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'نسبة المرتجعات' : "Returns Percentage"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {returns.percentage ? `${returns.percentage.toFixed(2)}%` : '0%'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Daily Sales Table */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {language === 'ar' ? 'المبيعات اليومية' : 'Daily Sales'}
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'التاريخ' : 'Date'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'عدد الطلبات' : 'Orders'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'متوسط قيمة الطلب' : 'Avg Order'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'إجمالي المبيعات' : 'Revenue'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.daily_sales.map((day, index) => (
                                <tr key={day.date} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {(() => {
                                            const date = new Date(day.date);
                                            const dayNum = date.getDate().toString().padStart(2, '0');
                                            const monthNum = (date.getMonth() + 1).toString().padStart(2, '0');
                                            const yearNum = date.getFullYear();
                                            
                                            if (language === 'ar') {
                                                const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
                                                return `${toArabicNumeral(dayNum)}/${toArabicNumeral(monthNum)}/${toArabicNumeral(yearNum)}`;
                                            }
                                            return `${dayNum}/${monthNum}/${yearNum}`;
                                        })()}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatNumber(day.total_orders)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(day.avg_order_value)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(day.total_revenue)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Top Items Table */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {language === 'ar' ? 'أفضل المنتجات مبيعاً' : 'Top Selling Items'}
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'المنتج' : 'Item'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'الكمية' : 'Quantity'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'إجمالي المبيعات' : 'Revenue'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.top_items.map((item, index) => (
                                <tr key={`${item.name}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {language === 'ar' ? item.name_ar : item.name}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatNumber(item.total_quantity)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(item.total_revenue)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SalesReport;
